/**
 * Server-Sent Events parsing.
 *
 * Pure and dependency-free so it can be tested directly.
 *
 * The subtlety that matters: a frame ends at a *blank line*, and the SSE spec allows
 * CRLF, LF or CR as the line terminator. Splitting on "\n\n" alone silently matches
 * nothing against a CRLF stream - which is what most servers, including this one, send -
 * so the client parses zero events and simply shows nothing.
 */

import type { RunEvent } from "@/lib/types"

/** Blank-line frame terminator: CRLF CRLF, LF LF, or CR CR. */
const FRAME_BOUNDARY = /\r\n\r\n|\n\n|\r\r/
const LINE_BREAK = /\r\n|\n|\r/

/**
 * Split whatever has been buffered into complete frames.
 *
 * Returns the frames plus the unconsumed remainder, since a chunk from the network
 * routinely ends mid-frame.
 */
export function splitFrames(buffer: string): { frames: string[]; rest: string } {
  const frames: string[] = []
  let rest = buffer

  for (;;) {
    const boundary = FRAME_BOUNDARY.exec(rest)
    if (!boundary) break
    frames.push(rest.slice(0, boundary.index))
    rest = rest.slice(boundary.index + boundary[0].length)
  }
  return { frames, rest }
}

/**
 * Parse one frame's `data:` payload.
 *
 * Comment lines (heartbeats) and unknown fields are ignored, so adding an event type
 * or field later cannot break an older client.
 */
export function parseFrame(frame: string): RunEvent | null {
  const data: string[] = []

  for (const line of frame.split(LINE_BREAK)) {
    if (!line || line.startsWith(":")) continue
    if (line.startsWith("data:")) data.push(line.slice(5).trimStart())
  }
  if (data.length === 0) return null

  try {
    return JSON.parse(data.join("\n")) as RunEvent
  } catch {
    return null
  }
}
